import React, { useMemo, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import type { MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  CtaBtn,
  MultiSelect,
  SecondaryBtn,
  StickyFooter,
  Select,
} from '@tmapy/style-guide';

import type { DmxConfig, OmittedFormItemProps } from '../../types';
import type { InputProps } from '../../hooks/useInput';
import { useInput, useMultiSelect } from '../../hooks';

import type { DocsListQuery, DocsListResponse } from '../../openapi/operations/docsList';
import { PrejimkaDmvsSourceEnum } from '../../openapi/types/PrejimkaDmvsSourceEnum';
import { SvuCrossNames } from '../../openapi/types/SvuCrossNames';

import { setDocListFilter } from '../../redux/filter/actions';
import { setMenuRouteFilterId } from '../../redux/menu/actions';

import { DOC_STATE, DOC_STATES_FILTER_DMVS_CROSS, FLOW_CROSS } from '../constants';

import {
  docFormMessages,
  docStateMessages,
  docFilterMessages,
  docSVUCrossNamesMessages,
  docListMessages,
  docInternalInfoMessages,
} from './docsMessages';
import { useCanManageDocInternalInfo } from './DocInternalInfo';

const messagesParams = {
  id: 'CROSS',
};

type DocFiltersProps = {
  filters: DocsListQuery;
  customerType: DmxConfig['customerType'];
  dispatch: Dispatch;
};

export const DocFilterDmvsCross: React.FC<DocFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  // ID
  const id = useInput<InputProps, number>({
    name: 'id',
    initialValue: filters.id,
    label: formatMessage(docFormMessages.id),
  });

  // NAME
  const name = useInput<InputProps>({
    name: 'name__icontains',
    initialValue: filters.name__icontains,
    label: formatMessage(docFormMessages.name),
  });

  // DESCRIPTION
  const description = useInput<InputProps>({
    name: 'description__icontains',
    initialValue: filters.description__icontains,
    label: formatMessage(docFormMessages.description),
  });

  // CREATED (from)
  const [createdGte, setCreateGt] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGt('');
    },
  };

  const handleCreateGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGt(eventData.value);
  };

  // CREATED (to)
  const [createdLt, setCreateLt] = useState(filters.created__lt);
  const created__lt = {
    name: 'created__lt',
    clear: () => {
      setCreateLt('');
    },
  };

  const handleCreateLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLt(eventData.value);
  };

  // FINISHED__GTE
  const [finishedGte, setFinishedGte] = useState(filters.finished__gte);
  const finished__gte = {
    name: 'finished__gte',
    clear: () => {
      setFinishedGte('');
    },
  };
  const handleFinishedGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedGte(eventData.value);
  };
  // FINISHED__LTE
  const [finishedLte, setFinishedLte] = useState(filters.finished__lte);
  const finished__lte = {
    name: 'finished__lte',
    clear: () => {
      setFinishedLte('');
    },
  };
  const handleFinishedLteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedLte(eventData.value);
  };

  // DMVS_ID_PODANI
  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani__icontains',
    initialValue: filters.dmvs_id_podani__icontains,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });

  // DMVS_SOURCE
  const dmvsSourceItems = useSelectMessages({} as Record<string, string>, PrejimkaDmvsSourceEnum);
  const dmvs_source = useInput({
    name: 'dmvs_source',
    initialValue: filters.dmvs_source,
    items: dmvsSourceItems,
    label: formatMessage(docFormMessages.dmvs_source),
  });

  // SUBJEKT_FULL_NAME
  const subjektFullName = useInput<InputProps>({
    name: 'subjekt_full_name',
    initialValue: filters.subjekt_full_name,
    label: formatMessage(docFormMessages.subjekt_jmeno),
  });

  // GEODESIST_FULL_NAME
  const geodesistFullName = useInput<InputProps>({
    name: 'geodesist_full_name',
    initialValue: filters.geodesist_full_name,
    label: formatMessage(docFormMessages.geodesist),
  });

  // EDITOR_MANAGER_FULL_NAME
  const editorManagerFullName = useInput<InputProps>({
    name: 'editor_manager_full_name',
    initialValue: filters.editor_manager_full_name,
    label: formatMessage(docFormMessages.editor_manager),
  });

  // STATE
  const stateItems = useSelectMessages(
    docStateMessages,
    DOC_STATES_FILTER_DMVS_CROSS as any,
    messagesParams,
  );

  const prepareStateItems = useCallback(
    (val: DOC_STATE) => {
      return stateItems.filter(
        (state) => unaccentAndLower(state.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [stateItems],
  );
  const stateInitialValues = useMemo(() => {
    return filters.state__in?.length
      ? stateItems.filter((state) => filters.state__in!.includes(state.value))
      : [];
  }, [stateItems, filters.state__in]);

  const state__in = useMultiSelect<
    MultiSelectProps<DocsListResponse> | OmittedFormItemProps,
    (typeof stateItems)[0]
  >({
    name: 'state__in',
    label: formatMessage(docFormMessages.state),
    initialValue: stateInitialValues,
    prepareItems: prepareStateItems,
    renderValue: (state: (typeof stateItems)[0]) => state.label,
    renderItem: (state: (typeof stateItems)[0]) => state.label,
  });

  // SVU/KRAJ
  const svuItems = useSelectMessages(docSVUCrossNamesMessages, SvuCrossNames as any);
  const prepareSVUItems = useCallback(
    (val: SvuCrossNames) => {
      return svuItems.filter(
        (svu) => unaccentAndLower(svu.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [svuItems],
  );
  const svuInitialValues = useMemo(() => {
    return filters.svu_cross_names__in?.length
      ? svuItems.filter((svu) => filters.svu_cross_names__in!.includes(svu.value))
      : [];
  }, [svuItems, filters.svu_cross_names__in]);

  const svu_cross_names__in = useMultiSelect<
    MultiSelectProps<DocsListResponse> | OmittedFormItemProps,
    (typeof svuItems)[0]
  >({
    name: 'svu_cross_names__in',
    label: formatMessage(docListMessages.docListColumnSvu),
    initialValue: svuInitialValues,
    prepareItems: prepareSVUItems,
    renderValue: (svu: (typeof svuItems)[0]) => svu.label,
    renderItem: (svu: (typeof svuItems)[0]) => svu.label,
  });

  const responsiblePersonFullName = useInput<InputProps>({
    name: 'responsible_person_full_name',
    initialValue: filters.responsible_person_full_name,
    label: formatMessage(docInternalInfoMessages.responsiblePerson),
  });
  const canManageInternalInfo = useCanManageDocInternalInfo();

  const filterClearScope = [
    name,
    description,
    state__in,
    svu_cross_names__in,
    id,
    created__gte,
    created__lt,
    finished__gte,
    finished__lte,
    geodesistFullName,
    subjektFullName,
    editorManagerFullName,
    dmvs_id_podani,
    dmvs_source,
    responsiblePersonFullName,
  ];

  const handleClearBtnClick = useCallback(() => {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(
      setDocListFilter({
        flow__in: FLOW_CROSS,
      }),
    );
    dispatch(setMenuRouteFilterId('managerDocListDmvsCross'));
  }, [dispatch]);

  function handleSubmit() {
    dispatch(
      setDocListFilter({
        name__icontains: name.value,
        description__icontains: description.value,
        state__in: state__in.values.length
          ? state__in.values.map((state) => state.value)
          : undefined,
        svu_cross_names__in: svu_cross_names__in.values.length
          ? svu_cross_names__in.values.map((svu) => svu.value)
          : undefined,
        id: id.value,
        flow__in: FLOW_CROSS,
        created__gte: createdGte,
        created__lt: createdLt,
        finished__gte: finishedGte,
        finished__lte: finishedLte,
        geodesist_full_name: geodesistFullName.value,
        subjekt_full_name: subjektFullName.value,
        editor_manager_full_name: editorManagerFullName.value,
        responsible_person_full_name: responsiblePersonFullName.value,
        dmvs_id_podani__icontains: dmvs_id_podani.value,
        dmvs_source: dmvs_source.value,
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormRow>
          <FormItem {...name}>
            <Input {...name} />
          </FormItem>
          <FormItem {...description}>
            <Input {...description} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...state__in}>
            <MultiSelect {...state__in} />
          </FormItem>
          <FormItem {...id}>
            <Input {...id} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...svu_cross_names__in}>
            <MultiSelect {...svu_cross_names__in} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem label={formatMessage(docFormMessages.createdGte)}>
            <Input
              type='date'
              name='created__gte'
              value={createdGte}
              onChange={handleCreateGtChange}
            />
          </FormItem>
          <FormItem label={formatMessage(docFormMessages.createdLt)}>
            <Input
              type='date'
              name='created__lt'
              value={createdLt}
              onChange={handleCreateLtChange}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem label={formatMessage(docFormMessages.finishedGte)}>
            <Input
              type='date'
              name='finished__gte'
              value={finishedGte}
              onChange={handleFinishedGteChange}
            />
          </FormItem>
          <FormItem label={formatMessage(docFormMessages.finishedLte)}>
            <Input
              type='date'
              name='finished__lte'
              value={finishedLte}
              onChange={handleFinishedLteChange}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...dmvs_id_podani}>
            <Input {...dmvs_id_podani} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...dmvs_source}>
            <Select hasEmptyItem {...dmvs_source} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...subjektFullName}>
            <Input {...subjektFullName} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...geodesistFullName}>
            <Input {...geodesistFullName} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...editorManagerFullName}>
            <Input {...editorManagerFullName} />
          </FormItem>
        </FormRow>

        {canManageInternalInfo && (
          <FormRow>
            <FormItem {...responsiblePersonFullName}>
              <Input {...responsiblePersonFullName} />
            </FormItem>
          </FormRow>
        )}
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...docFilterMessages.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...docFilterMessages.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
